import { Alert, Grid, Toolbar, Typography } from "@mui/material";
import { RichTextInput, RichTextInputToolbar } from "ra-input-rich-text";
import { useEffect, useState } from "react";
import {
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  ChipField,
  Create,
  CreateButton,
  DatagridConfigurable,
  DeleteButton,
  Edit,
  EditButton,
  ExportButton,
  FilterButton,
  ImageField,
  List,
  NumberInput,
  Pagination,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  SaveButton,
  SelectArrayInput,
  SelectColumnsButton,
  SelectInput,
  ShowButton,
  SimpleFormIterator,
  SingleFieldList,
  TabbedForm,
  TextField,
  TextInput,
  TopToolbar,
  TranslatableInputs,
  WrapperField,
  isEmpty,
  required,
  useRecordContext,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import ImageArrayInputWithPreview from "../components/ImageArrayInputWithPreview";
import { PricesTabHeader } from "../components/PricesTabHeader";
import ProductSelectionInputWithPreview from "../components/ProductSelectionInputWithPreview";
import { SummaryTabHeader } from "../components/SummaryTabHeader";
import { TranslationsTabHeader } from "../components/TranslationsTabHeader";
import LocalisedDateField from "../components/localisedDateField";
import dataProvider from "../providers/grapheneDataProvider2";
import { Locale } from "./locales";

const isFalsy = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return true;
  } else if (typeof value === "object") {
    return isEmpty(value);
  }
  return false;
};

const isTruthy = (value: any) => {
  return !isFalsy(value);
};

const validateBundleEdit = (bundlePre: GQL.V2BundlepresSetInput) => {
  let errors: any = {};
  if (isTruthy(bundlePre)) {
    if (!bundlePre?.products?.length) {
      errors.products = "Required";
    }
    if (!bundlePre?.locales?.length) {
      errors.locales = "Required";
    }
    if (isTruthy(bundlePre.prices) && isTruthy(bundlePre?.prices?.stripePriceIdsExtended)) {
      Object.entries(bundlePre?.prices?.stripePriceIdsExtended).forEach(([currency, prices]: [any, any]) => {
        prices.forEach((price: any) => {
          if (price["type"] == "recurring") {
            if (
              isFalsy(price["recurringInterval"]) ||
              isFalsy(price["recurringIntervalCount"]) ||
              isFalsy(price["recurringTrialPeriodDays"])
            ) {
              let error = {
                [currency]: `Recurring values are missing`,
              };
              errors.prices = {
                stripePriceIdsExtended: error,
              };
            }
          }
        });
      });
    }
  }
  return errors;
};

const normalizeBundleBeforeSave = (bundlePre: any) => {
  if (!bundlePre?.prices?.stripePriceIdsExtended) {
    return bundlePre;
  }

  const stripePriceIdsExtended = Object.fromEntries(
    Object.entries(bundlePre.prices.stripePriceIdsExtended).map(([currency, prices]) => [
      currency,
      Array.isArray(prices)
        ? prices.map((price: any) => ({
            ...price,
            type: price?.type || "one_time",
            recurringInterval: price?.recurringInterval || "",
            recurringIntervalCount: price?.recurringIntervalCount || 0,
            recurringTrialPeriodDays: price?.recurringTrialPeriodDays || 0,
          }))
        : prices,
    ])
  );

  return {
    ...bundlePre,
    prices: {
      ...bundlePre.prices,
      stripePriceIdsExtended,
    },
  };
};

const BundleTranslationsForm = () => {
  const record = useRecordContext();
  const localeIds = useWatch({ name: "locales" });
  const [translationLocaleKeys, setTranslationLocaleKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (localeIds) {
      let t_keys: string[] = ["en-GB"];
      dataProvider.getMany("locales", { id__in: localeIds }).then(({ data }) => {
        data.forEach((locale: Locale) => {
          t_keys.push(`${locale.language}-${locale.country}`);
        });
        setTranslationLocaleKeys([...new Set(t_keys)]);
      });
    }
  }, [localeIds]);

  if (!localeIds || localeIds.length === 0) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Please select locales
      </Alert>
    );
  }

  if (!translationLocaleKeys) {
    return <p>Loading...</p>;
  }
  return (
    <>
      <Alert severity="info">"en-GB" fields are required</Alert>
      <TranslatableInputs
        locales={translationLocaleKeys}
        defaultLocale={"en-GB"}
        sx={{ minWidth: "600px" }}
        groupKey="translations"
        fullWidth
      >
        <RichTextInput
          fullWidth
          source="translations.description"
          label="Description"
          toolbar={<RichTextInputToolbar size="small" />}
        />
        <TextInput source="translations.nameOrder" label="Order name" helperText="Name used in order details" />
        <TextInput
          source="translations.nameMarketing"
          label="Marketing name"
          helperText="Name used in marketing (emails etc.)"
        />
        <TextInput
          source="translations.cardRecommendationTitle"
          label="Card recommendation title"
          helperText="Label shown on the subscription card, e.g. Unser Bestseller"
        />
        <ArrayInput source="translations.bulletedList" label="Subscription detail list">
          <SimpleFormIterator inline>
            <TextInput source="text" label="Text" />
            <TextInput source="tooltip" label="Tooltip" multiline minRows={2} />
          </SimpleFormIterator>
        </ArrayInput>
        <ImageArrayInputWithPreview source="translations.images" />
        <ArrayInput source="translations.labels" label="Labels">
          <SimpleFormIterator inline>
            <TextInput source="text" />
            <TextInput source="colour" />
          </SimpleFormIterator>
        </ArrayInput>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2, mb: 1, width: "100%" }}>
          Related Products
        </Typography>
        <ReferenceArrayInput source="translations.relatedCartCrossSellBundles" reference="v2Bundlepres" perPage={100}>
          <AutocompleteArrayInput
            label="Related Cart Cross Sell Bundles"
            optionText={getBundleOptionLabelWithCountry}
            filterToQuery={filterToQueryBundleName}
            sx={{ minWidth: "100%" }}
          />
        </ReferenceArrayInput>
        <ReferenceArrayInput source="translations.productDetailPageGroup" reference="pdpGroup" perPage={100}>
          <AutocompleteArrayInput
            label="Product Detail Page Group"
            optionText={getPdpGroupOptionLabelWithCountry}
            filterToQuery={filterToQueryPdpGroupPageTag}
            sx={{ minWidth: "100%" }}
          />
        </ReferenceArrayInput>
      </TranslatableInputs>
    </>
  );
};

interface ObjectLiteral {
  [key: string]: any;
}

const BundlePricesForm = () => {
  const { setValue, getValues } = useFormContext();
  const products = useWatch({ name: "products" });
  const localeIds = useWatch({ name: "locales" });
  const [priceLocaleKeys, setPriceLocaleKeys] = useState<string[] | null>(null);

  useEffect(() => {
    if (!priceLocaleKeys || !products?.length) {
      return;
    }

    const productIds = products.map((product: any) => product?.productId || product?.id || product).filter(Boolean);
    const existingPricesByLocale = getValues("prices.stripePriceIdsExtended") || {};
    let hasChanges = false;

    const nextPricesByLocale = Object.fromEntries(
      priceLocaleKeys.map((localeKey) => {
        const localePrices = Array.isArray(existingPricesByLocale[localeKey]) ? existingPricesByLocale[localeKey] : [];
        const localeProductIds = new Set(localePrices.map((price: any) => price?.productId).filter(Boolean));
        const missingProductPrices = productIds
          .filter((productId: string) => !localeProductIds.has(productId))
          .map((productId: string) => ({
            productId,
            unitAmount: 0,
            type: "one_time",
            recurringInterval: "",
            recurringIntervalCount: 0,
            recurringTrialPeriodDays: 0,
          }));

        if (missingProductPrices.length > 0 || !Array.isArray(existingPricesByLocale[localeKey])) {
          hasChanges = true;
        }

        return [localeKey, [...localePrices, ...missingProductPrices]];
      })
    );

    if (hasChanges) {
      setValue(
        "prices.stripePriceIdsExtended",
        { ...existingPricesByLocale, ...nextPricesByLocale },
        { shouldDirty: true }
      );
    }
  }, [getValues, priceLocaleKeys, products, setValue]);

  useEffect(() => {
    if (localeIds) {
      let p_keys: string[] = [];
      dataProvider.getMany("locales", { id__in: localeIds }).then(({ data }) => {
        data.forEach((locale: Locale) => {
          p_keys.push(`${locale.currency}-${locale.country}`);
        });
        setPriceLocaleKeys([...new Set(p_keys)]);
      });
    }
  }, [localeIds]);

  if (!localeIds || localeIds.length === 0) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Please select locales
      </Alert>
    );
  }

  if (!priceLocaleKeys) {
    return <p>Loading...</p>;
  }

  if (!products || products.length === 0) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Please select products
      </Alert>
    );
  }

  return (
    <>
      <TranslatableInputs locales={priceLocaleKeys} defaultLocale={priceLocaleKeys[0]} groupKey="prices" fullWidth>
        <NumberInput
          label="Monthly subscription price difference"
          source="prices.priceAmountSubDifference"
          helperText="Stored in cents, e.g. 250 = 2.50"
          step={1}
        />
        <ArrayInput label="Item Prices" source="prices.stripePriceIdsExtended">
          <SimpleFormIterator
            getItemLabel={(index) => `${index + 1}.`}
            inline
            fullWidth
            disableAdd
            disableRemove
            disableClear
            disableReordering
          >
            <ReferenceField source="productId" reference="v2Products">
              <ImageField source="image" />
            </ReferenceField>
            <ReferenceField source="productId" reference="v2Products" sx={{ width: "300px" }}>
              <>
                <TextField source="name" />
                <br />
                <TextField source="productId" />
              </>
            </ReferenceField>
            <NumberInput label="Price" source="unitAmount" step={1} validate={required()} sx={{ width: "100px" }} />
            <SelectInput
              source="type"
              choices={[
                { id: "one_time", name: "One Time" },
                { id: "recurring", name: "Recurring" },
              ]}
              emptyText=""
              emptyValue=""
              validate={required()}
              defaultValue="one_time"
            />
            <SelectInput
              source="recurringInterval"
              choices={[
                { id: "month", name: "Month" },
                { id: "year", name: "Year" },
              ]}
              emptyText=""
              emptyValue=""
              defaultValue=""
              sx={{ width: "150px" }}
            />
            <NumberInput
              label="Recurring Interval Count"
              source="recurringIntervalCount"
              defaultValue={0}
              sx={{ width: "150px" }}
            />
            <NumberInput
              label="Recurring Trial Period Days"
              source="recurringTrialPeriodDays"
              defaultValue={0}
              sx={{ width: "200px" }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </TranslatableInputs>
    </>
  );
};

export const NewBundleCreate = () => {
  return (
    <Create transform={normalizeBundleBeforeSave}>
      <TabbedForm syncWithLocation={false} validate={validateBundleEdit}>
        <TabbedForm.Tab label={<SummaryTabHeader />}>
          <TextInput source="name" fullWidth />
          <TextInput source="image" label="Image" fullWidth />
          <ReferenceArrayInput source="locales" reference="locales" perPage={100}>
            <AutocompleteArrayInput
              validate={required()}
              label="Locales"
              optionText={(record) => `${record.name}`}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
          <ReferenceArrayInput source="products" reference="v2Products" perPage={100}>
            <AutocompleteArrayInput
              validate={required()}
              label="Products"
              optionText={(record) => `${record.name}`}
              filterToQuery={filterToQueryActiveTags}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
          <ReferenceArrayInput
            source="bundleTags"
            reference="productTags"
            perPage={500}
            filter={filterBundleTags}
            sort={{ field: "name", order: "ASC" }}
          >
            <SelectArrayInput optionText="name" sx={{ minWidth: "400px" }} />
          </ReferenceArrayInput>
          <NumberInput label="Index" source="index" />
          <BooleanInput label="Is crossed price visible?" source="isCrossedPriceVisible" />
          <ReferenceArrayInput source="relatedCartCrossSellBundles" reference="v2Bundlepres" perPage={100}>
            <AutocompleteArrayInput
              label="Related Cart Cross Sell Bundles"
              optionText={getBundleOptionLabelWithCountry}
              filterToQuery={filterToQueryBundleName}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
          <ReferenceArrayInput source="productDetailPageGroup" reference="pdpGroup" perPage={100}>
            <AutocompleteArrayInput
              label="Product Detail Page Group"
              optionText={getPdpGroupOptionLabelWithCountry}
              filterToQuery={filterToQueryPdpGroupPageTag}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
        </TabbedForm.Tab>
        <TabbedForm.Tab label={<TranslationsTabHeader />}>
          <BundleTranslationsForm />
        </TabbedForm.Tab>
        {/*<TabbedForm.Tab label={<PricesTabHeader />}>
          <BundlePricesForm />
        </TabbedForm.Tab>*/}
      </TabbedForm>
    </Create>
  );
};

const ReadOnlyToolbar = () => {
  const record = useRecordContext();
  return (
    <Toolbar>
      <Alert severity="info" sx={{ width: "100%" }}>
        This Bundle was published <LocalisedDateField record={record} field="modifiedAt" /> <br />
        <a href={`/v2Bundlepres/${record.id}`}>Click here</a> to edit the corresponding BundlePre
      </Alert>
    </Toolbar>
  );
};

const BundleEditToolbar = () => {
  return (
    <Toolbar sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}>
      <Grid container justifyContent={"space-between"}>
        <Grid item>
          <SaveButton alwaysEnable />
        </Grid>
        <Grid item>
          <DeleteButton />
        </Grid>
      </Grid>
    </Toolbar>
  );
};

export const NewBundleEdit = (props: any) => {
  return (
    <Edit mutationMode="pessimistic" transform={normalizeBundleBeforeSave}>
      <TabbedForm
        syncWithLocation={false}
        validate={validateBundleEdit}
        toolbar={props.hasSave ? <BundleEditToolbar /> : <ReadOnlyToolbar />}
      >
        <TabbedForm.Tab label={<SummaryTabHeader />}>
          <TextInput source="name" fullWidth />
          <TextInput source="image" label="Image" fullWidth />
          <ReferenceArrayInput source="locales" reference="locales">
            <AutocompleteArrayInput validate={required()} label="Locales" optionText={(record) => `${record.name}`} />
          </ReferenceArrayInput>
          <ArrayInput fullWidth label="Products" source="products" helperText="Products in this bundle">
            <SimpleFormIterator fullWidth getItemLabel={(index) => `${index + 1}.`} inline>
              <ProductSelectionInputWithPreview source="" resource={props.hasSave ? "v2Productpres" : "v2Products"} />
            </SimpleFormIterator>
          </ArrayInput>

          <ReferenceArrayInput
            source="bundleTags"
            reference="productTags"
            perPage={500}
            filter={filterBundleTags}
            sort={{ field: "name", order: "ASC" }}
          >
            <SelectArrayInput optionText="name" sx={{ minWidth: "400px" }} />
          </ReferenceArrayInput>
          <NumberInput label="Index" source="index" />
          <BooleanInput label="Is crossed price visible" source="isCrossedPriceVisible" />
          <ReferenceArrayInput source="relatedCartCrossSellBundles" reference="v2Bundlepres" perPage={100}>
            <AutocompleteArrayInput
              label="Related Cart Cross Sell Bundles"
              optionText={getBundleOptionLabelWithCountry}
              filterToQuery={filterToQueryBundleName}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
          <ReferenceArrayInput source="productDetailPageGroup" reference="pdpGroup" perPage={100}>
            <AutocompleteArrayInput
              label="Product Detail Page Group"
              optionText={getPdpGroupOptionLabelWithCountry}
              filterToQuery={filterToQueryPdpGroupPageTag}
              sx={{ minWidth: "100%" }}
            />
          </ReferenceArrayInput>
        </TabbedForm.Tab>
        <TabbedForm.Tab label={<TranslationsTabHeader />}>
          <BundleTranslationsForm />
        </TabbedForm.Tab>

        <TabbedForm.Tab label={<PricesTabHeader />}>
          <BundlePricesForm />
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
};

const MyActions = ({ showCreate = false }) => {
  return (
    <TopToolbar>
      {showCreate ? <CreateButton /> : ""}
      <FilterButton />
      <SelectColumnsButton />
      <ExportButton />
    </TopToolbar>
  );
};

const MyPagination = (props: any) => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} {...props} />;
const filterToQueryTags = (searchText: string) => ({
  name__icontains: `${searchText}`,
});
const filterToQueryBundleName = (searchText: string) => ({
  name__icontains: `${searchText}`,
});
const getBundleOptionLabelWithCountry = (record: any) => {
  const name = record?.name || record?.bundleId || "";
  const fromRelatedObject =
    record?.relatedCartCrossSellBundles?.country ||
    (Array.isArray(record?.relatedCartCrossSellBundles)
      ? record.relatedCartCrossSellBundles.find(
          (item: any) => item?.id === record?.id || item?.bundleId === record?.bundleId || item?.name === record?.name
        )?.country
      : undefined);
  const country = record?.country || fromRelatedObject;

  return country ? `${name}(${String(country).toLowerCase()})` : name;
};
const getPdpGroupOptionLabelWithCountry = (record: any) => {
  const pageTag = record?.pageTagId || "";
  const country = record?.country;

  return country ? `${pageTag} (${String(country).toLowerCase()})` : pageTag;
};
const filterToQueryPdpGroupPageTag = (searchText: string) => ({
  page_tag_id__icontains: `${searchText}`,
});
const filterToQueryActiveTags = (searchText: string) => ({
  name__icontains: `${searchText}`,
  active: true,
});
const filterToQueryBundleTags = (searchText: string) => ({
  name__icontains: `${searchText}`,
  active: true,
  category__in: ["product_page", "related_products"],
});
const filterBundleTags = { active: true, category__in: ["product_page", "related_products"] };
const filterTags = { active: true };
const bundleFilters = [
  <TextInput label="ID" source="pk" alwaysOn />,
  <TextInput label="Name" source="name__icontains" alwaysOn />,
  <ReferenceArrayInput
    label="Page Tags in"
    source="bundleTags__contains"
    reference="productTags"
    alwaysOn
    perPage={500}
    filter={filterBundleTags}
  >
    <AutocompleteArrayInput label="Page Tags in" filterToQuery={filterToQueryBundleTags} sx={{ minWidth: "400px" }} />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    label="Page Tags not in"
    source="bundleTags__nin"
    reference="productTags"
    alwaysOn
    perPage={500}
    filter={filterBundleTags}
  >
    <AutocompleteArrayInput
      label="Page Tags not in"
      filterToQuery={filterToQueryBundleTags}
      sx={{ minWidth: "400px" }}
    />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    label="Tags in"
    source="tags__contains"
    reference="productTags"
    alwaysOn
    perPage={500}
    filter={filterTags}
  >
    <AutocompleteArrayInput label="Tags in" filterToQuery={filterToQueryActiveTags} sx={{ minWidth: "400px" }} />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    label="Tags not in"
    source="tags__nin"
    reference="productTags"
    alwaysOn
    perPage={500}
    filter={filterTags}
  >
    <AutocompleteArrayInput label="Tags not in" filterToQuery={filterToQueryActiveTags} sx={{ minWidth: "400px" }} />
  </ReferenceArrayInput>,
];

export const NewBundleList = (props: any) => (
  <List filters={bundleFilters} actions={<MyActions {...props} />} pagination={<MyPagination />}>
    <DatagridConfigurable
      omit={["nameOrderEn", "nameMarketingEn", "nameOrderDe", "nameMarketingDe", "isCrossedPriceVisible"]}
    >
      <TextField source="bundleId" />

      <ImageField sx={{ "& img": { maxWidth: 100, maxHeight: 50, objectFit: "contain" } }} source="image" />
      <TextField source="name" />

      <ReferenceArrayField label="Page Tags" reference="productTags" source="bundleTags">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>

      <ReferenceArrayField label="Tags" reference="productTags" source="tags">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>

      <TextField lable="Index" source="index" />
      <WrapperField label="Actions">{props.showCreate ? <EditButton /> : <ShowButton />}</WrapperField>
    </DatagridConfigurable>
  </List>
);
